import { call, CallEffect, put, PutEffect, SelectEffect } from 'redux-saga/effects'

import {
  changePasswordFailureAction,
  changePasswordSuccessAction,
  createUserFailureAction,
  createUserSuccessAction,
  updateUserFailureAction,
  updateUserSuccessAction,
  fetchCustomerCreditCardFailureAction,
  fetchCustomerCreditCardSuccessAction,
  UpdateUserRequestAction,
  ChangePasswordRequestAction,
  FetchCustomerCreditCardRequestAction,
  CreateUserRequestAction,
} from './actions'

import { CONFLICT, UNAUTHORIZED } from 'src/constants/httpcodes'
import {
  createUser,
  updateUser,
  changePassword,
  fetchCustomerCreditCard,
  CreateUserDtoResponse,
  UpdateUserDtoResponse,
  FetchCustomerCreditCardResponse,
} from './requests'
import { displayAlertAction } from 'src/comps/AlertMessage/actions'
import { sendLoginEvent } from 'client/contexts/Login/helpers'
import { subscribe } from '../SubscriptionStatus/requests'
import { SUBSCRIPTION_SIGNUP_PAGE } from 'src/constants/subscriptionSources'
import { AnyAction } from 'redux'

function isGenericError<TSuccess, TError extends { errorCode: string }>(
  data: TSuccess | TError,
  status: number
): data is TError {
  return status >= 400
}

export function* createUserSaga(
  action: CreateUserRequestAction
): Generator<
  CallEffect<CreateUserDtoResponse> | PutEffect<AnyAction> | SelectEffect,
  void,
  CreateUserDtoResponse | string
> {
  try {
    const response = yield call(createUser, action.payload)

    if (typeof response === 'string') {
      yield put(createUserFailureAction({ errorCode: response }))
      return
    }

    const { data, status } = response

    if (status === CONFLICT.status) {
      yield put(displayAlertAction({ messageId: 'errorAlreadyRegistered' }))
      yield put(createUserFailureAction(data))
      return
    }

    if (isGenericError(data, status)) {
      yield put(displayAlertAction({ messageId: 'errorDefault' }))
      yield put(createUserFailureAction(data))
      return
    }

    yield put(
      createUserSuccessAction({
        created: true,
        data: {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          userId: data.id,
        },
      })
    )
    yield put(displayAlertAction({ messageId: 'signUpSuccess', severity: 'success' }))
    yield put(sendLoginEvent())

    yield call(subscribe, {
      email: action.payload.data.email,
      hostname: action.payload.hostname,
      language: action.payload.data.language,
      market: action.payload.data.market,
      source: SUBSCRIPTION_SIGNUP_PAGE,
    })
  } catch (error: unknown) {
    yield put(displayAlertAction({ messageId: 'errorDefault' }))
    yield put(
      createUserFailureAction({
        errorCode: error instanceof Error ? error.message : 'Unknown error',
      })
    )
  }
}

export function* updateUserSaga(
  action: UpdateUserRequestAction
): Generator<
  CallEffect<UpdateUserDtoResponse> | PutEffect<AnyAction>,
  void,
  UpdateUserDtoResponse
> {
  try {
    const { data, status } = yield call(updateUser, action.payload)

    if (isGenericError(data, status)) {
      yield put(displayAlertAction({ messageId: 'errorDefault' }))
      yield put(updateUserFailureAction(data))
      return
    }

    yield put(displayAlertAction({ messageId: 'successUpdatedUser', severity: 'success' }))
    yield put(updateUserSuccessAction({ updated: true, data: action.payload.data }))
  } catch (error) {
    yield put(displayAlertAction({ messageId: 'errorDefault' }))
    yield put(
      updateUserFailureAction({
        errorCode: error instanceof Error ? error.message : 'Unknown error',
      })
    )
  }
}

export function* changePasswordSaga(
  action: ChangePasswordRequestAction
): Generator<
  CallEffect<UpdateUserDtoResponse> | PutEffect<AnyAction>,
  void,
  UpdateUserDtoResponse
> {
  try {
    const { data, status } = yield call(changePassword, action.payload)

    if (isGenericError(data, status)) {
      yield put(displayAlertAction({ messageId: 'errorDefault' }))
      yield put(changePasswordFailureAction(data))
      return
    }

    yield put(displayAlertAction({ messageId: 'successChangePassword', severity: 'success' }))
    yield put(changePasswordSuccessAction({ passwordChanged: true }))
  } catch (error) {
    yield put(displayAlertAction({ messageId: 'errorDefault' }))
    yield put(
      changePasswordFailureAction({
        errorCode: error instanceof Error ? error.message : 'Unknown error',
      })
    )
  }
}

export function* fetchCustomerCreditCardSaga(
  action: FetchCustomerCreditCardRequestAction
): Generator<
  CallEffect<FetchCustomerCreditCardResponse> | PutEffect<AnyAction>,
  void,
  FetchCustomerCreditCardResponse
> {
  try {
    const { data, status } = yield call(fetchCustomerCreditCard, action.payload)

    if (status >= UNAUTHORIZED.status) {
      yield put(fetchCustomerCreditCardFailureAction({ errorCode: UNAUTHORIZED.message }))
      return
    }

    if (isGenericError(data, status)) {
      yield put(fetchCustomerCreditCardFailureAction(data))
      return
    }

    yield put(fetchCustomerCreditCardSuccessAction({ creditCard: data }))
  } catch (error) {
    yield put(
      fetchCustomerCreditCardFailureAction({
        errorCode: error instanceof Error ? error.message : 'Unknown error',
      })
    )
  }
}
